<template>
  <div class="pdf-viewer-wrapper position-relative" @contextmenu.stop.prevent @selectstart.stop.prevent
    @copy.stop.prevent @dragstart.stop.prevent>
    <!-- 1. Canvas Mode Controls Bar -->
    <div v-if="!useIframeFallback"
      class="pdf-toolbar d-flex align-items-center justify-content-between px-3 py-2 border-bottom bg-light">
      <div class="d-flex align-items-center gap-2">
        <button type="button" class="btn btn-sm btn-outline-secondary d-flex align-items-center"
          :disabled="currentPage <= 1 || loading" @click="prevPage" title="Halaman Sebelumnya">
          <i class="isax isax-arrow-left-2"></i>
        </button>
        <span class="fs-13 fw-medium text-muted">
          Halaman <strong class="text-dark">{{ currentPage }}</strong> / {{ totalPages || 1 }}
        </span>
        <button type="button" class="btn btn-sm btn-outline-secondary d-flex align-items-center"
          :disabled="currentPage >= totalPages || loading" @click="nextPage" title="Halaman Berikutnya">
          <i class="isax isax-arrow-right-3"></i>
        </button>
      </div>

      <div class="d-flex align-items-center gap-2">
        <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="scale <= 0.6 || loading"
          @click="zoomOut" title="Perkecil">
          <i class="isax isax-minus"></i>
        </button>
        <span class="fs-12 text-muted" style="min-width: 45px; text-align: center;">
          {{ Math.round(scale * 100) }}%
        </span>
        <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="scale >= 2.5 || loading"
          @click="zoomIn" title="Perbesar">
          <i class="isax isax-add"></i>
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="loading" @click="resetZoom"
          title="Reset Ukuran">
          Reset
        </button>
      </div>

      <div class="d-flex align-items-center gap-2">
        <span class="badge bg-light text-muted border fs-11 d-none d-md-flex align-items-center">
          <i class="isax isax-hand me-1"></i> Klik & Geser untuk Membaca
        </span>
        <span class="badge bg-secondary-subtle text-secondary border fs-11 d-flex align-items-center">
          <i class="isax isax-shield-tick me-1"></i> Mode Baca Terproteksi
        </span>
      </div>
    </div>

    <!-- 2. Canvas Mode Container -->
    <div v-if="!useIframeFallback" ref="canvasContainer"
      class="pdf-canvas-container d-flex justify-content-center align-items-start p-3 position-relative"
      :class="{ 'is-dragging': isDragging }"
      style="height: 70vh; min-height: 590px; overflow: auto; background: #525659;"
      @mousedown="startDrag"
      @mousemove="onDrag"
      @mouseup="stopDrag">
      <!-- Loading indicator -->
      <div v-if="loading"
        class="position-absolute top-50 start-50 translate-middle d-flex flex-column align-items-center text-white"
        style="z-index: 10;">
        <div class="spinner-border text-light mb-2" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="fs-13 mb-0">Memuat dokumen PDF...</p>
      </div>

      <!-- Canvas Render Target -->
      <canvas ref="pdfCanvas" class="pdf-canvas shadow bg-white rounded"
        :style="{ display: loading ? 'none' : 'block' }"></canvas>
    </div>

    <!-- 3. Iframe Fallback Container (Jika Canvas tidak didukung) -->
    <div v-else class="position-relative w-100" style="height: 70vh; min-height: 590px;" @contextmenu.stop.prevent>
      <iframe :key="iframeFallbackUrl" :src="iframeFallbackUrl" width="100%" height="100%"
        style="border: none; height: 100%; min-height: 590px"></iframe>
    </div>
  </div>
</template>

<script>
import axios from "@/utils/axios";
import { markRaw } from "vue";

export default {
  name: "PdfViewer",
  props: {
    src: {
      type: String,
      required: true,
    },
    isStudent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 0,
      scale: 1.2,
      loading: true,
      rendering: false,
      renderPending: null,
      useIframeFallback: false,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      scrollStartX: 0,
      scrollStartY: 0,
    };
  },
  created() {
    this.pdfDoc = null;
  },
  computed: {
    iframeFallbackUrl() {
      if (!this.src) return "";
      if (this.isStudent) {
        return `${this.src}#toolbar=0&navpanes=0`;
      }
      return this.src;
    },
  },
  watch: {
    src: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.initPdf();
        }
      },
    },
  },
  mounted() {
    window.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("mouseup", this.stopDrag);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("mouseup", this.stopDrag);
    if (this.pdfDoc) {
      try {
        this.pdfDoc.destroy();
      } catch (e) {
        // ignore
      }
      this.pdfDoc = null;
    }
  },
  methods: {
    async loadPdfJsLibrary() {
      if (window.pdfjsLib) {
        return window.pdfjsLib;
      }
      return new Promise((resolve, reject) => {
        const scriptId = "pdfjs-lib-cdn";
        let script = document.getElementById(scriptId);
        if (script) {
          script.addEventListener("load", () => resolve(window.pdfjsLib));
          script.addEventListener("error", () =>
            reject(new Error("Gagal mengunduh script PDF.js"))
          );
          return;
        }

        script = document.createElement("script");
        script.id = scriptId;
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
        script.onload = () => {
          if (window.pdfjsLib) {
            try {
              // Buka worker via Blob untuk mem-bypass CORS restriction pada worker
              const workerBlob = new Blob(
                [
                  'importScripts("https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js");',
                ],
                { type: "application/javascript" }
              );
              window.pdfjsLib.GlobalWorkerOptions.workerSrc =
                URL.createObjectURL(workerBlob);
            } catch {
              window.pdfjsLib.GlobalWorkerOptions.workerSrc =
                "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
            }
            resolve(window.pdfjsLib);
          } else {
            reject(new Error("pdfjsLib tidak terdefinisi"));
          }
        };
        script.onerror = () =>
          reject(new Error("Koneksi gagal saat memuat library viewer PDF"));
        document.head.appendChild(script);
      });
    },

    async initPdf() {
      this.loading = true;
      this.useIframeFallback = false;
      this.currentPage = 1;

      try {
        const pdfjsLib = await this.loadPdfJsLibrary();

        let pdfData;
        try {
          const response = await axios.get(this.src, {
            responseType: "arraybuffer",
          });
          pdfData = new Uint8Array(response.data);
        } catch (fetchErr) {
          console.warn("Axios fetch gagal, mencoba fetch langsung:", fetchErr);
          const raw = await fetch(this.src);
          if (!raw.ok) {
            throw new Error(`HTTP error ${raw.status}: ${raw.statusText}`);
          }
          const buf = await raw.arrayBuffer();
          pdfData = new Uint8Array(buf);
        }

        const loadingTask = pdfjsLib.getDocument({ data: pdfData });
        const rawDoc = await loadingTask.promise;
        this.pdfDoc = markRaw(rawDoc);
        this.totalPages = this.pdfDoc.numPages;

        this.loading = false;
        await this.$nextTick();
        this.renderCurrentPage();
      } catch (err) {
        console.warn("Gagal render via Canvas PDF.js, fallback ke iframe:", err);
        // Otomatis fallback ke iframe agar dokumen tetap bisa dibaca user
        this.useIframeFallback = true;
        this.loading = false;
      }
    },

    async renderCurrentPage() {
      if (!this.pdfDoc || !this.$refs.pdfCanvas) return;

      if (this.rendering) {
        this.renderPending = this.currentPage;
        return;
      }

      this.rendering = true;

      try {
        const page = await this.pdfDoc.getPage(this.currentPage);
        const canvas = this.$refs.pdfCanvas;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        const viewport = page.getViewport({ scale: this.scale });

        // High DPI scaling (Retina / Sharp rendering)
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = Math.floor(viewport.width) + "px";
        canvas.style.height = Math.floor(viewport.height) + "px";

        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const renderContext = {
          canvasContext: ctx,
          viewport: viewport,
        };

        await page.render(renderContext).promise;
      } catch (err) {
        console.error("Gagal merender halaman PDF:", err);
      } finally {
        this.rendering = false;
        if (this.renderPending !== null) {
          const next = this.renderPending;
          this.renderPending = null;
          this.renderCurrentPage();
        }
      }
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.renderCurrentPage();
        this.scrollToTop();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.renderCurrentPage();
        this.scrollToTop();
      }
    },

    zoomIn() {
      if (this.scale < 2.5) {
        this.scale = Math.round((this.scale + 0.2) * 10) / 10;
        this.renderCurrentPage();
      }
    },

    zoomOut() {
      if (this.scale > 0.6) {
        this.scale = Math.round((this.scale - 0.2) * 10) / 10;
        this.renderCurrentPage();
      }
    },

    resetZoom() {
      this.scale = 1.2;
      this.renderCurrentPage();
    },

    scrollToTop() {
      if (this.$refs.canvasContainer) {
        this.$refs.canvasContainer.scrollTop = 0;
      }
    },

    startDrag(e) {
      if (e.button !== 0 || !this.$refs.canvasContainer) return;
      this.isDragging = true;
      this.dragStartX = e.clientX;
      this.dragStartY = e.clientY;
      this.scrollStartX = this.$refs.canvasContainer.scrollLeft;
      this.scrollStartY = this.$refs.canvasContainer.scrollTop;
    },

    onDrag(e) {
      if (!this.isDragging || !this.$refs.canvasContainer) return;
      e.preventDefault();
      const dx = e.clientX - this.dragStartX;
      const dy = e.clientY - this.dragStartY;
      this.$refs.canvasContainer.scrollLeft = this.scrollStartX - dx;
      this.$refs.canvasContainer.scrollTop = this.scrollStartY - dy;
    },

    stopDrag() {
      this.isDragging = false;
    },

    handleKeyDown(event) {
      if (["ArrowLeft", "ArrowUp"].includes(event.key)) {
        if (this.currentPage > 1) {
          event.preventDefault();
          this.prevPage();
        }
      } else if (["ArrowRight", "ArrowDown"].includes(event.key)) {
        if (this.currentPage < this.totalPages) {
          event.preventDefault();
          this.nextPage();
        }
      }
    },
  },
};
</script>

<style scoped>
.pdf-viewer-wrapper {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.pdf-toolbar {
  border-radius: 6px 6px 0 0;
}

.pdf-canvas-container {
  border-radius: 0 0 6px 6px;
  cursor: grab;
  scroll-behavior: auto;
}

.pdf-canvas-container.is-dragging {
  cursor: grabbing;
  user-select: none;
}

.pdf-canvas {
  display: block;
  margin: 0 auto;
  pointer-events: none;
}
</style>
